import React, { useState } from 'react';
import { PartyPopper, Cake, Church, Calendar, MapPin, Clock, Users, Ticket, X, Instagram, Facebook, Phone, Music, Camera, FerrisWheel, Sparkles, Star, Heart, Palette, Network as Firework, CheckCircle2 } from 'lucide-react';
import type { Event } from '../lib/types';

interface EventFormData {
  title: string;
  description: string;
  longDescription?: string;
  attractions: string[];
  highlights: string[];
  date: string;
  location: string;
  image_url: string;
  status: 'draft' | 'published';
}

const initialFormData: EventFormData = {
  title: '',
  description: '',
  longDescription: '',
  attractions: [],
  highlights: [],
  date: new Date().toISOString().split('T')[0],
  location: '',
  image_url: '',
  status: 'draft'
};

export default function Services() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<EventFormData>(initialFormData);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedService, setSelectedService] = useState<number | null>(null);

  const categories = [
    { name: 'Matrimoni', icon: <Heart className="h-6 w-6" /> },
    { name: 'Feste di 18 Anni', icon: <Cake className="h-6 w-6" /> },
    { name: 'Battesimi', icon: <Church className="h-6 w-6" /> },
    { name: 'Comunioni', icon: <Heart className="h-6 w-6" /> },
    { name: 'Cresime', icon: <Heart className="h-6 w-6" /> },
    { name: 'Feste di Laurea', icon: <Star className="h-6 w-6" /> },
    { name: 'Eventi Aziendali', icon: <Users className="h-6 w-6" /> },
    { name: 'Feste Private', icon: <PartyPopper className="h-6 w-6" /> },
    { name: 'Sagre e Fiere', icon: <Firework className="h-6 w-6" /> },
    { name: 'Feste di Piazza', icon: <Firework className="h-6 w-6" /> },
    { name: 'Compleanni', icon: <Cake className="h-6 w-6" /> },
    { name: 'Eventi Scolastici', icon: <Users className="h-6 w-6" /> }
  ];

  const closeModal = () => {
    setSelectedService(null);
    document.body.style.overflow = 'auto';
    document.body.style.paddingRight = '';
  };

  const openModal = (index: number) => {
    setSelectedService(index);
    document.body.style.overflow = 'hidden';
    // Calculate scrollbar width
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  };

  const services = [
    {
      icon: <FerrisWheel className="h-10 w-10 text-pink-600" />,
      title: 'Giostre e Attrazioni per Eventi',
      description: 'Trasforma il tuo evento in un\'esperienza magica con le nostre attrazioni certificate.',
      features: [
        'Attrazioni Certificate CE',
        'Personale Qualificato',
        'Montaggio Professionale',
        'Assistenza Dedicata'
      ],
      image: 'https://images.unsplash.com/photo-1597466599360-3b9775841aec',
      details: {
        description: 'Rendiamo unico ogni evento con le nostre attrazioni selezionate. Dal celebre Tagada alle eleganti giostre vintage, ogni attrazione è scelta e personalizzata per creare l\'atmosfera perfetta per il tuo evento. Che sia un matrimonio, un compleanno o una festa aziendale, garantiamo divertimento e sicurezza per tutti i tuoi ospiti.',
        includes: [
          'Sopralluogo tecnico gratuito',
          'Trasporto e logistica professionale',
          'Team specializzato per montaggio',
          'Operatori certificati e formati',
          'Copertura assicurativa completa',
          'Supporto tecnico durante l\'evento',
          'Certificazioni CE aggiornate',
          'Controlli di sicurezza pre-evento'
        ],
        attractions: [
          'Tagada - L\'attrazione regina per eventi giovani',
          'Dischi Volanti - Adrenalina e divertimento',
          'Giostra Vintage - Eleganza per matrimoni',
          'Mini Seggiolini - Sicurezza per bambini',
          'Trenino Incantato - Magia per famiglie',
          'Carosello Classico - Atmosfera retrò'
        ],
        extras: [
          'Illuminazione LED personalizzata',
          'Sistema audio professionale',
          'Decorazioni evento coordinate',
          'Allestimento area tematico',
          'Photobooth e area selfie',
          'Zona relax arredata',
          'Corner ristoro attrezzato',
          'Gestione flussi e sicurezza'
        ]
      }
    },
    {
      icon: <Music className="h-10 w-10 text-pink-600" />,
      title: 'Intrattenimento e Spettacolo',
      description: 'Creiamo l\'atmosfera perfetta con musica live, DJ set e animazione professionale.',
      features: [
        'DJ e Band Professionisti',
        'Audio-Luci Professionali',
        'Animazione Personalizzata',
        'Effetti Speciali'
      ],
      image: 'https://images.unsplash.com/photo-1470225620780-dba8ba36b745',
      details: {
        description: 'Trasformiamo ogni evento in uno spettacolo indimenticabile. I nostri professionisti dell\'intrattenimento creano la giusta atmosfera per ogni momento della tua festa, dal sottofondo elegante durante l\'aperitivo alla musica coinvolgente per il ballo.',
        includes: [
          'DJ set personalizzato',
          'Band live professionista',
          'Cantanti per cerimonia',
          'Animatori specializzati',
          'Impianto audio Line Array',
          'Lighting design scenografico'
        ],
        attractions: [
          'DJ Set con visual show',
          'Band live multi-genere',
          'Lounge band per aperitivo',
          'Karaoke professionale HD',
          'Animazione interattiva',
          'Spettacoli tematici'
        ],
        extras: [
          'Effetti CO2 e pirotecnici',
          'Laser show programmato',
          'Video mapping artistico',
          'Macchine del fumo DMX',
          'Bolle di sapone giganti',
          'Coriandoli e stelle filanti'
        ]
      }
    },
    {
      icon: <Camera className="h-10 w-10 text-pink-600" />,
      title: 'Servizi Foto e Video',
      description: 'Catturiamo ogni emozione con servizi fotografici e video di alta qualità.',
      features: [
        'Fotografi Professionisti',
        'Video 4K Cinema',
        'Drone Certificato ENAC',
        'Consegna Express'
      ],
      image: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32',
      details: {
        description: 'Il nostro team di professionisti dell\'immagine utilizza le più moderne tecnologie per immortalare ogni momento del tuo evento. Dalla fotografia artistica alle riprese cinematografiche, creiamo ricordi indimenticabili con uno stile unico e contemporaneo.',
        includes: [
          'Due fotografi professionisti',
          'Videografo cinematografico',
          'Pilota drone certificato',
          'Album design esclusivo',
          'Montaggio same-day',
          'Cloud storage privato'
        ],
        attractions: [
          'Reportage fotografico',
          'Film cinematografico',
          'Riprese aeree 4K',
          'Photobooth interattivo',
          'Video highlights',
          'Social media content'
        ],
        extras: [
          'Stampa foto istantanea',
          'Video teaser same-day',
          'Instagram reels',
          'Slideshow live',
          'Book digitale',
          'Stampe fine art'
        ]
      }
    }
  ];

  return (
    <div id="servizi" className="py-32 bg-gradient-to-b from-white via-pink-50/50 to-white relative overflow-hidden min-h-screen">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-pink-200/30 via-transparent to-transparent"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-purple-200/30 via-transparent to-transparent"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-gradient-to-br from-pink-200/40 to-purple-200/40 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-gradient-to-br from-purple-200/40 to-pink-200/40 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-br from-pink-200/20 to-purple-200/20 rounded-full blur-2xl animate-float"></div>
        <div className="absolute bottom-1/4 right-1/4 w-32 h-32 bg-gradient-to-br from-purple-200/20 to-pink-200/20 rounded-full blur-2xl animate-float-reverse"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-block relative">
            <span className="absolute -inset-8 bg-gradient-to-r from-pink-600/40 via-purple-600/30 to-pink-600/40 blur-2xl rounded-full animate-pulse hidden sm:block"></span>
            <span className="absolute -inset-12 bg-gradient-to-r from-pink-400/30 via-purple-400/20 to-pink-400/30 blur-3xl rounded-full animate-pulse hidden sm:block" style={{ animationDelay: '0.5s' }}></span>
            <h2 className="relative text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight gradient-text-animated px-4 sm:px-0 drop-shadow-sm">
              Servizi Esclusivi per Eventi
            </h2>
          </div>
          <p className="mt-6 sm:mt-8 text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4 sm:px-0 leading-relaxed font-medium">
            Trasformiamo ogni occasione in un evento indimenticabile con servizi professionali e attrazioni esclusive
          </p>
          
          {/* Category showcase - now purely visual */}
          <div className="mt-8 sm:mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {categories.map((category) => (
              <div
                key={category.name}
                className="group relative flex flex-col items-center justify-center p-4 sm:p-6 rounded-xl text-xs sm:text-sm font-medium bg-gradient-to-br from-white/80 to-pink-50/30 hover:from-pink-50 hover:to-purple-50 text-pink-600 border border-pink-100/30 hover:border-pink-300/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 hover:scale-102 backdrop-blur-sm cursor-default"
              >
                <span className="absolute inset-0 bg-gradient-to-br from-pink-400/5 via-pink-400/10 to-purple-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-xl"></span>
                <span className="relative mb-3">
                  <span className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-gradient-to-br from-pink-100/90 to-purple-100/90 rounded-2xl group-hover:scale-110 transition-all duration-500 shadow-lg group-hover:shadow-xl backdrop-blur-sm">
                    <span className="text-pink-600 transform group-hover:scale-110 transition-all duration-500">
                      {category.icon}
                    </span>
                  </span>
                  <span className="absolute inset-0 bg-gradient-to-br from-pink-400/20 to-purple-400/20 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 animate-pulse"></span>
                </span>
                <span className="font-semibold tracking-wide text-center text-gray-800 group-hover:text-pink-600 transition-colors duration-300">{category.name}</span>
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-gradient-to-r from-pink-500/0 via-pink-500/50 to-pink-500/0 opacity-0 group-hover:opacity-100 transition-all duration-500"></span>
              </div>
            ))}
          </div>
          
          <div className="mt-8 sm:mt-12 flex justify-center">
            <div className="inline-flex items-center px-6 py-3 rounded-xl text-sm font-medium bg-gradient-to-r from-green-50/80 to-emerald-50/80 text-green-600 border border-green-200/30 shadow-lg backdrop-blur-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></span>
              Sopralluogo e preventivo gratuito in 24h
            </div>
          </div>
        </div>

        {/* Main Services Section */}
        <div className="mt-16 sm:mt-24 grid grid-cols-1 gap-8 sm:gap-12 lg:grid-cols-3">
          {services.map((service, index) => (
            <div
              key={index}
              className="group relative bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:rotate-1 border border-pink-100/50 hover:border-pink-200 overflow-hidden cursor-pointer"
              onClick={() => openModal(index)}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-pink-50/50 to-purple-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="relative p-8">
                <div className="flex items-center mb-6">
                  <div className="relative">
                    {service.icon}
                    <div className="absolute inset-0 bg-pink-400/20 blur-xl rounded-full animate-pulse"></div>
                  </div>
                  <h3 className="ml-4 text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center group/item">
                      <CheckCircle2 className="h-5 w-5 text-pink-500 mr-3 group-hover/item:scale-110 transition-transform duration-300" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-500 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></span>
                    Disponibile
                  </span>
                  <button className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-full text-white bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl group-hover:shadow-pink-500/25">
                    Scopri di più
                    <svg className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal for service details */}
        {selectedService !== null && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
              <div className="fixed inset-0 bg-gray-900/75 backdrop-blur-sm transition-opacity" onClick={closeModal}>
                <div className="absolute inset-0 bg-gradient-to-br from-pink-900/20 to-purple-900/20"></div>
              </div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

              <div className="relative inline-block w-full max-w-4xl p-6 my-8 text-left align-middle transition-all transform bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl hover:shadow-pink-500/10 border border-pink-100/20 animate-modal-enter">
                <div className="absolute top-4 right-4 z-10">
                  <button
                    onClick={closeModal}
                    className="group bg-white/10 backdrop-blur-sm rounded-full p-3 text-gray-400 hover:text-pink-500 focus:outline-none hover:bg-pink-50/20 transition-all duration-300 hover:scale-110 hover:rotate-90"
                  >
                    <span className="sr-only">Chiudi</span>
                    <X className="h-6 w-6 transform transition-transform duration-300 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-400/20 to-purple-400/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </button>
                </div>

                <div className="relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-pink-50/50 to-purple-50/50 opacity-50"></div>
                  <div className="relative">
                    <div className="sm:flex sm:items-start">
                      <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-pink-50 to-purple-50 sm:mx-0 sm:h-20 sm:w-20 animate-pulse-slow relative group">
                        <div className="absolute inset-0 bg-gradient-to-r from-pink-400/30 to-purple-400/30 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        {services[selectedService].icon}
                      </div>
                      <div className="mt-3 text-center sm:mt-0 sm:ml-6 sm:text-left">
                        <h3 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600">
                          {services[selectedService].title}
                        </h3>
                        <p className="mt-2 text-lg text-gray-600">
                          {services[selectedService].details.description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
                      <div>
                        <h4 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600 mb-4">Servizi Inclusi</h4>
                        <ul className="space-y-3">
                          {services[selectedService].details.includes.map((item, idx) => (
                            <li key={idx} className="flex items-start group p-2 rounded-lg hover:bg-pink-50/50 transition-all duration-300">
                              <CheckCircle2 className="h-5 w-5 text-pink-500 mt-0.5 mr-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                              <span className="text-gray-600">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600 mb-4">Caratteristiche</h4>
                        <ul className="space-y-3">
                          {services[selectedService].details.attractions.map((item, idx) => (
                            <li key={idx} className="flex items-start group p-2 rounded-lg hover:bg-pink-50/50 transition-all duration-300">
                              <Star className="h-5 w-5 text-pink-500 mt-0.5 mr-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                              <span className="text-gray-600">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-8">
                      <h4 className="text-xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 to-purple-600 mb-4">Extra e Personalizzazioni</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {services[selectedService].details.extras.map((extra, idx) => (
                          <div key={idx} className="group bg-gradient-to-r from-pink-50 to-purple-50 p-4 rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-[1.02] hover:-rotate-1">
                            <Sparkles className="h-5 w-5 text-pink-500 mb-2 group-hover:scale-110 transition-transform duration-300" />
                            <p className="text-gray-700">{extra}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-8 pt-8 border-t border-pink-100/50">
                      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex items-center space-x-2 text-gray-500">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                          <span>Preventivo gratuito in 24h</span>
                        </div>
                        <a
                          href="https://wa.me/+393274517331?text=Ciao! Sono interessato/a al servizio di giostre ed attrazioni. Vorrei maggiori informazioni."
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-8 py-4 border border-transparent text-base font-medium rounded-full text-white bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 transform hover:scale-102 transition-all duration-300 shadow-lg hover:shadow-xl group relative overflow-hidden order-1 sm:order-2"
                        >
                          <Phone className="w-5 h-5 mr-2" />
                          Contattaci su WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}